import {
  Card,
  Pagination,
  Table,
  Tag,
  message,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { Api } from "@model";
import { ColumnsType } from "antd/es/table";

interface IPayment {
  _id: string;
  userID: {
    _id: string;
    name?: string;
    email?: string;
    avatar?: string;
  } | string;
  platform: "apple" | "google" | "bazaar";
  productId: string;
  orderId?: string;
  purchaseToken?: string;
  receiptData?: string;
  environment?: "Sandbox" | "Production";
  status: "pending" | "validated" | "failed" | "refunded" | "expired";
  isSubscription: boolean;
  expiresAt?: Date | null;
  validatedAt?: Date | null;
  createdAt: Date;
}

const PaymentListPage = () => {
  const [payments, setPayments] = useState<IPayment[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Api.get(
        `/api/admin/panel/payments?page=${page}&limit=${limit}`
      );
      if (response?.data?.data) {
        setPayments(response.data.data.payments || []);
        setTotal(response.data.data.total || 0);
      } else {
        message.error("Invalid response format");
      }
    } catch (error: any) {
      console.error("Failed to fetch payments:", error);
      message.error(
        error?.response?.data?.error || "Failed to fetch payments"
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "validated":
        return "green";
      case "pending":
        return "orange";
      case "failed":
        return "red";
      case "refunded":
        return "volcano";
      case "expired":
        return "default";
      default:
        return "blue";
    }
  };

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case "apple":
        return "purple";
      case "google":
        return "blue";
      case "bazaar":
        return "green";
      default:
        return "default";
    }
  };

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return "-";
    const d = new Date(date);
    return d.toLocaleString();
  };

  const getUserName = (userID: IPayment["userID"]) => {
    if (typeof userID === "string") {
      return userID;
    }
    return userID?.name || userID?.email || userID?._id || "-";
  };

  const columns: ColumnsType<IPayment> = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: Date) => formatDate(date),
      sorter: (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      defaultSortOrder: "descend",
      width: 180,
    },
    {
      title: "User",
      dataIndex: "userID",
      key: "userID",
      render: (userID: IPayment["userID"]) => getUserName(userID),
      width: 200,
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      render: (platform: string) => (
        <Tag color={getPlatformColor(platform)}>
          {platform.toUpperCase()}
        </Tag>
      ),
      width: 100,
    },
    {
      title: "Product ID",
      dataIndex: "productId",
      key: "productId",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
      width: 120,
    },
    {
      title: "Subscription",
      dataIndex: "isSubscription",
      key: "isSubscription",
      render: (isSubscription: boolean) => (
        <Tag color={isSubscription ? "blue" : "default"}>
          {isSubscription ? "Yes" : "No"}
        </Tag>
      ),
      width: 100,
    },
    {
      title: "Environment",
      dataIndex: "environment",
      key: "environment",
      render: (env: string | undefined) =>
        env ? (
          <Tag color={env === "Production" ? "green" : "orange"}>
            {env}
          </Tag>
        ) : (
          "-"
        ),
      width: 120,
    },
    {
      title: "Expires At",
      dataIndex: "expiresAt",
      key: "expiresAt",
      render: (date: Date | null) => formatDate(date),
      width: 180,
    },
    {
      title: "Validated At",
      dataIndex: "validatedAt",
      key: "validatedAt",
      render: (date: Date | null) => formatDate(date),
      width: 180,
    },
    {
      title: "Order ID",
      dataIndex: "orderId",
      key: "orderId",
      render: (orderId: string | undefined) => orderId || "-",
      width: 200,
    },
  ];

  return (
    <Card title="Payments" style={{ margin: "20px" }}>
      <Table
        columns={columns}
        dataSource={payments}
        rowKey="_id"
        loading={loading}
        pagination={false}
        scroll={{ x: 1500 }}
      />
      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          current={page}
          pageSize={limit}
          total={total}
          onChange={(newPage, newLimit) => {
            setPage(newPage);
            if (newLimit) setLimit(newLimit);
          }}
          showSizeChanger
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} of ${total} payments`
          }
        />
      </div>
    </Card>
  );
};

export default PaymentListPage;

